/**
 * Deterministische toevalsgenerator.
 *
 * De regelkernel mag `Math.random()` niet gebruiken: een potje moet exact
 * reproduceerbaar zijn uit zijn seed + event-log (zie plan §4). Daarom draagt de
 * spelstand zijn eigen generatorstand met zich mee, en levert elke trekking een
 * *nieuwe* stand op in plaats van interne mutatie.
 *
 * Algoritme: SplitMix64 op BigInt — klein, goed getest, en dezelfde uitkomst op elke
 * machine (in tegenstelling tot drijvende-komma-varianten).
 */

const MASK64 = (1n << 64n) - 1n;
const GOLDEN_GAMMA = 0x9e3779b97f4a7c15n;

export interface Rng {
  /** Interne stand. Hoort bij de spelstand en gaat mee in snapshots. */
  readonly state: bigint;
}

/** Maak een generator uit een seed (bijvoorbeeld het `rng_seed` van een potje). */
export function createRng(seed: bigint | number | string): Rng {
  let s: bigint;
  if (typeof seed === 'bigint') s = seed;
  else if (typeof seed === 'number') s = BigInt(Math.trunc(seed));
  else s = hashString(seed);
  return { state: s & MASK64 };
}

/** Zet een willekeurige tekst om in een 64-bits seed (FNV-1a). */
export function hashString(text: string): bigint {
  let hash = 0xcbf29ce484222325n;
  for (let i = 0; i < text.length; i++) {
    hash ^= BigInt(text.charCodeAt(i));
    hash = (hash * 0x100000001b3n) & MASK64;
  }
  return hash;
}

/** Eén stap: geeft een nieuwe stand plus 64 willekeurige bits. */
function next(rng: Rng): { rng: Rng; value: bigint } {
  const state = (rng.state + GOLDEN_GAMMA) & MASK64;
  let z = state;
  z = ((z ^ (z >> 30n)) * 0xbf58476d1ce4e5b9n) & MASK64;
  z = ((z ^ (z >> 27n)) * 0x94d049bb133111ebn) & MASK64;
  z = z ^ (z >> 31n);
  return { rng: { state }, value: z };
}

/**
 * Geheel getal in [0, bound). Gebruikt verwerping zodat elke uitkomst even
 * waarschijnlijk is — een simpele modulo geeft de laagste waarden een streepje voor.
 */
export function nextInt(rng: Rng, bound: number): { rng: Rng; value: number } {
  if (!Number.isInteger(bound) || bound <= 0) {
    throw new RangeError(`bound moet een positief geheel getal zijn, kreeg ${bound}`);
  }
  const b = BigInt(bound);
  const limit = MASK64 - (MASK64 % b);
  let current = rng;
  for (;;) {
    const step = next(current);
    current = step.rng;
    if (step.value < limit) {
      return { rng: current, value: Number(step.value % b) };
    }
  }
}

/** Dobbelsteenworp: geheel getal in [1, sides]. */
export function roll(rng: Rng, sides: number): { rng: Rng; value: number } {
  const result = nextInt(rng, sides);
  return { rng: result.rng, value: result.value + 1 };
}

/** Kies één element uit een niet-lege lijst. */
export function pick<T>(rng: Rng, items: readonly T[]): { rng: Rng; value: T } {
  if (items.length === 0) throw new RangeError('pick() op een lege lijst');
  const result = nextInt(rng, items.length);
  return { rng: result.rng, value: items[result.value]! };
}

/** Fisher-Yates: geeft een nieuwe, geschudde lijst terug (het origineel blijft heel). */
export function shuffle<T>(rng: Rng, items: readonly T[]): { rng: Rng; value: T[] } {
  const out = [...items];
  let current = rng;
  for (let i = out.length - 1; i > 0; i--) {
    const step = nextInt(current, i + 1);
    current = step.rng;
    const j = step.value;
    const a = out[i]!;
    const b = out[j]!;
    out[i] = b;
    out[j] = a;
  }
  return { rng: current, value: out };
}
